import { useState } from 'react';

import { Button, Card, CardHeader, Dropdown, Field, Input, StatusPill } from '@/design-system';
import {
    PLATFORM_REASON_MIN_LENGTH,
    TENANT_STATUS_LABELS,
    TENANT_STATUS_TONES,
    type Plan,
    type PlatformTenant,
} from '@/entities/tenant';
import { formatCurrencyFromCents, formatDayLong } from '@/shared/lib';

import { adminCopy } from '../copy';

export interface TenantDetailProps {
    tenant: PlatformTenant;
    plans: readonly Plan[];
    plansLoading?: boolean;
    onChangePlan: (planCode: string) => void;
    onSuspend: (reason: string) => void;
    onResume: () => void;
    saving?: boolean;
    notice?: string | null;
    error?: string | null;
}

/**
 * Eén organisatie: wie het is, welk abonnement, en of het bedrijf kan werken.
 *
 * Opschorten is destructief voor de klant - het hele bedrijf kan niet meer
 * werken - dus het gaat via een bevestiging die precies zegt wat er gebeurt, met
 * een verplichte reden van minimaal tien tekens (dezelfde regel als de API).
 */
export function TenantDetail({
    tenant,
    plans,
    plansLoading = false,
    onChangePlan,
    onSuspend,
    onResume,
    saving = false,
    notice = null,
    error = null,
}: TenantDetailProps) {
    const [confirming, setConfirming] = useState(false);
    const [reason, setReason] = useState('');
    const reasonValid = reason.trim().length >= PLATFORM_REASON_MIN_LENGTH;

    const facts: Array<{ label: string; value: string }> = [
        { label: adminCopy.tenantEmail, value: tenant.email ?? adminCopy.unknown },
        { label: adminCopy.tenantPhone, value: tenant.phone ?? adminCopy.unknown },
        { label: adminCopy.tenantTimezone, value: tenant.timezone ?? adminCopy.unknown },
        {
            label: adminCopy.tenantSince,
            value: tenant.createdAt ? formatDayLong(tenant.createdAt) : adminCopy.unknown,
        },
        ...(tenant.trialEndsAt
            ? [{ label: adminCopy.tenantTrialEnds, value: formatDayLong(tenant.trialEndsAt) }]
            : []),
    ];

    return (
        <Card clip as="section" aria-labelledby="tenant-detail-title">
            <CardHeader
                title={<span id="tenant-detail-title">{tenant.name}</span>}
                description={tenant.slug}
                action={
                    <StatusPill tone={TENANT_STATUS_TONES[tenant.status]} size="sm">
                        {TENANT_STATUS_LABELS[tenant.status]}
                    </StatusPill>
                }
            />

            <div className="px-4 py-[14px]">
                <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3">
                    {facts.map((fact) => (
                        <div key={fact.label} className="min-w-0">
                            <dt className="text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                                {fact.label}
                            </dt>
                            <dd className="m-0 mt-[2px] truncate text-[13px] text-[var(--text-primary)]">
                                {fact.value}
                            </dd>
                        </div>
                    ))}
                </dl>

                {tenant.status === 'suspended' && tenant.suspendedReason ? (
                    <p className="m-0 mt-3 rounded-[7px] border border-[var(--warn-border)] bg-[var(--warn-tint)] p-[10px] text-[13px] leading-[1.5] text-[var(--warn-text)]">
                        {adminCopy.tenantSuspendedOn(
                            tenant.suspendedAt ? formatDayLong(tenant.suspendedAt) : null,
                            tenant.suspendedReason,
                        )}
                    </p>
                ) : null}
            </div>

            <div className="border-t border-[var(--border-subtle)] px-4 py-[14px]">
                <Field label={adminCopy.planLabel} size="sm" className="max-w-[420px]">
                    <Dropdown
                        value={tenant.planCode ?? ''}
                        disabled={saving || plansLoading}
                        placeholder={
                            plansLoading
                                ? adminCopy.planLoading
                                : (tenant.planName ?? adminCopy.noPlan)
                        }
                        options={plans.map((plan) => ({
                            value: plan.code,
                            label: plan.name,
                            hint: adminCopy.planOptionHint(
                                formatCurrencyFromCents(plan.monthlyPriceCents),
                                plan.includedInspections,
                            ),
                        }))}
                        onChange={(code) => {
                            if (code !== '') {
                                onChangePlan(code);
                            }
                        }}
                    />
                </Field>

                {confirming ? (
                    <Card
                        variant="warn"
                        padding="md"
                        radius={9}
                        className="mt-3"
                        role="group"
                        aria-label={adminCopy.suspendConfirmLabel(tenant.name)}
                    >
                        <p className="m-0 text-[13px] leading-[1.5] text-[var(--warn-ink)]">
                            {adminCopy.suspendConfirm(tenant.name)}
                        </p>

                        <Field
                            label={adminCopy.reason}
                            size="sm"
                            className="mt-3"
                            error={
                                reason !== '' && !reasonValid
                                    ? adminCopy.reasonTooShort(PLATFORM_REASON_MIN_LENGTH)
                                    : undefined
                            }
                        >
                            <Input
                                value={reason}
                                placeholder={adminCopy.suspendPlaceholder}
                                onChange={(event) => setReason(event.target.value)}
                            />
                        </Field>

                        <div className="mt-3 flex flex-wrap gap-2">
                            <Button
                                variant="warn"
                                size="sm"
                                disabled={!reasonValid || saving}
                                onClick={() => {
                                    onSuspend(reason.trim());
                                    setConfirming(false);
                                    setReason('');
                                }}
                            >
                                {adminCopy.suspendYes}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setConfirming(false);
                                    setReason('');
                                }}
                            >
                                {adminCopy.cancel}
                            </Button>
                        </div>
                    </Card>
                ) : (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {tenant.status === 'suspended' ? (
                            <Button
                                variant="primary"
                                size="sm"
                                disabled={saving}
                                onClick={onResume}
                            >
                                {adminCopy.resume}
                            </Button>
                        ) : tenant.status === 'cancelled' ? null : (
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={saving}
                                onClick={() => setConfirming(true)}
                            >
                                {adminCopy.suspend}
                            </Button>
                        )}
                    </div>
                )}

                {notice ? (
                    <p
                        role="status"
                        className="m-0 mt-3 text-[12px] leading-[1.4] text-[var(--accent-dark)]"
                    >
                        {notice}
                    </p>
                ) : null}

                {error ? (
                    <p
                        role="alert"
                        className="m-0 mt-3 text-[12px] leading-[1.4] text-[var(--danger)]"
                    >
                        {error}
                    </p>
                ) : null}
            </div>
        </Card>
    );
}
